import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  filter,
  from,
  map,
  take,
  tap,
} from 'rxjs';

import { Language, SDK } from '@plugins/middleware-sdk';

export const languageToString = ({ lang, locale }: Language): string => {
  if (!lang) return 'en';
  if (!locale) return lang;
  return `${lang}-${locale}`;
};

export abstract class BrowserLanguageToken {
  abstract get locale$(): Observable<string>;
}

@Injectable({ providedIn: 'root' })
export class BrowserLanguageService extends BrowserLanguageToken {
  #locale = new BehaviorSubject<string | undefined>(undefined);
  #firstFetch = true;
  #isLoading = false;

  private sdk = inject(SDK);

  private get fetchAllowed(): boolean {
    return this.#firstFetch && !this.#isLoading;
  }

  get locale$(): Observable<string> {
    if (!this.fetchAllowed) {
      return this.#locale.asObservable().pipe(
        filter((locale): locale is string => locale !== undefined),
        take(1),
      );
    }
    this.#isLoading = true;
    return from(this.sdk.getBrowserLanguage()).pipe(
      map((language) => languageToString(language)),
      tap((locale) => {
        this.#locale.next(locale);
        this.#isLoading = false;
        this.#firstFetch = false;
      }),
    );
  }
}

/** For use in apps that are not embeded within a platform and have to
 * fetch the language settings directly from navigator. */
@Injectable({ providedIn: 'root' })
export class DirectBrowserLanguageService extends BrowserLanguageToken {
  // Copied from middleware-server/sdk
  protected getBrowserLanguage() {
    if (
      typeof window === 'undefined' ||
      typeof window.navigator === 'undefined'
    ) {
      return Promise.reject(new Error('window (navigator) is undefined'));
    }

    const browserLang = window.navigator.languages[0];

    if (browserLang.indexOf('-') !== -1) {
      const [lang, locale] = browserLang.split('-');
      return Promise.resolve({ lang, locale });
    }
    if (browserLang.indexOf('_') !== -1) {
      const [lang, locale] = browserLang.split('_');
      return Promise.resolve({ lang, locale });
    }
    return Promise.resolve({ lang: browserLang, locale: '' });
  }

  get locale$() {
    return from(this.getBrowserLanguage()).pipe(
      map((language) => languageToString(language)),
    );
  }
}

export class MockBrowserLanguageService extends BrowserLanguageToken {
  locale = new BehaviorSubject<string>('en');
  get locale$() {
    return this.locale.asObservable();
  }
}
